import type { Container, Newable } from "inversify";
import type { IModule, IService } from "@designboticteam/core";
import { type MapConfig, MapConfigService, MapStoreService, PmTilesService, type PmTilesSourceId, ProjectionService } from "@designboticteam/map";
import { MapLibreCameraViewService } from "./core/MapLibreCameraViewService";
import { MapLibreInstanceService } from "./core/MapLibreInstanceService";
import { MapLibreParcelsDataService, PARCELS_PMTILES_SOURCE_ID } from "./domain/MapLibreParcelsDataManager";
import { PmTilesRenderingService } from "./rendering/PmTilesRenderingService";

class MapLibreModule implements IModule {
	public readonly moduleName = "⛰️ MapLibreModule";

	private container: Container | null = null;
	private services: Array<Newable<IService>> = [
		MapStoreService,
		ProjectionService,
		PmTilesService,
		//
		MapLibreInstanceService,
		MapLibreCameraViewService,
		PmTilesRenderingService,
		//
		MapLibreParcelsDataService,
	];

	constructor(private readonly config: MapConfig = { origin: [0, 0], zoom: 1, style: "default" }) {}

	private getService<T extends IService>(Service: Newable<T>) {
		if (this.container === null) {
			throw new Error("Container not found.");
		}

		return this.container.get(Service) as unknown as T;
	}

	public register(container: Container): void {
		container.bind(MapConfigService).toConstantValue(new MapConfigService({ ...this.config }));

		this.services.forEach((x) => {
			container.bind(x).toSelf().inSingletonScope();
		});

		this.container = container;
	}

	public initialize(): void {
		this.services.forEach((x) => {
			this.getService(x).initialize();
		});

		const pmTilesService = this.getService(PmTilesService);
		for (const source of this.config.pmTilesSources ?? []) {
			pmTilesService.addLayer(source);
		}
	}

	public dispose(): void {
		[...this.services].reverse().forEach((x) => {
			this.getService(x).dispose();
		});
	}

	public get api() {
		const mapLibreInstanceService = this.getService(MapLibreInstanceService);
		const parcelsDataService = this.getService(MapLibreParcelsDataService);
		const cameraViewService = this.getService(MapLibreCameraViewService);
		const mapStoreService = this.getService(MapStoreService);
		const pmTilesService = this.getService(PmTilesService);
		const pmTilesRenderingService = this.getService(PmTilesRenderingService);

		return {
			maplibre: {
				store: mapStoreService.store,
				captureScreenshot: mapLibreInstanceService.captureScreenshot.bind(mapLibreInstanceService),
				captureMapState: mapLibreInstanceService.captureMapState.bind(mapLibreInstanceService),
				buildings: {
					setVisible: mapLibreInstanceService.setBuildingsVisible.bind(mapLibreInstanceService),
				},
				parcels: {
					sourceId: PARCELS_PMTILES_SOURCE_ID,
					layer: {
						setVisible: parcelsDataService.setVisible.bind(parcelsDataService),
						setLabelsVisible: parcelsDataService.setLabelsVisible.bind(parcelsDataService),
					},
					list: {
						setFilter: parcelsDataService.setFilter.bind(parcelsDataService),
						setSort: parcelsDataService.setSort.bind(parcelsDataService),
						setHoveredParcelId: parcelsDataService.setHoveredParcelId.bind(parcelsDataService),
						focusParcel: parcelsDataService.focusParcel.bind(parcelsDataService),
					},
					active: {
						clear: parcelsDataService.clearActiveParcel.bind(parcelsDataService),
						setPogSplitVisible: (isVisible: boolean) => {
							mapStoreService.setState((state) => ({ parcels: { ...state.parcels, isPogSplitVisible: isVisible } }));
						},
						setMpzpSplitVisible: (isVisible: boolean) => {
							mapStoreService.setState((state) => ({ parcels: { ...state.parcels, isMpzpSplitVisible: isVisible } }));
						},
						setObszarUzupelnieniaZabudowySplitVisible: (isVisible: boolean) => {
							mapStoreService.setState((state) => ({ parcels: { ...state.parcels, isObszarUzupelnieniaZabudowySplitVisible: isVisible } }));
						},
						setHoveredSplitZoneIndex: (zoneIndex: number | null) => {
							mapStoreService.setState((state) => ({ parcels: { ...state.parcels, hoveredSplitZoneIndex: zoneIndex } }));
						},
					},
					favourites: {
						toggle: parcelsDataService.toggleParcelSelection.bind(parcelsDataService),
						toggleById: parcelsDataService.toggleParcelSelectionById.bind(parcelsDataService),
					},
					visualization: {
						setHeightField: parcelsDataService.setVisualizationHeightField.bind(parcelsDataService),
						setHeightFactor: parcelsDataService.setVisualizationHeightFactor.bind(parcelsDataService),
						setColorField: parcelsDataService.setVisualizationColorField.bind(parcelsDataService),
						setPumDiffColors: parcelsDataService.setVisualizationPumDiffColors.bind(parcelsDataService),
						setHeightDiffColors: parcelsDataService.setVisualizationHeightDiffColors.bind(parcelsDataService),
						setHighlightOutsideMpzp: parcelsDataService.setVisualizationHighlightParcelsOutsideMpzp.bind(parcelsDataService),
					},
				},
				camera: {
					zoomIn: cameraViewService.zoomIn.bind(cameraViewService),
					zoomOut: cameraViewService.zoomOut.bind(cameraViewService),
					resetBearing: cameraViewService.resetBearing.bind(cameraViewService),
					resetPitch: cameraViewService.resetPitch.bind(cameraViewService),
					setProjection: cameraViewService.setProjection.bind(cameraViewService),
					resetView: cameraViewService.resetView.bind(cameraViewService),
					loadViewPreset: cameraViewService.loadViewPreset.bind(cameraViewService),
					flyTo: cameraViewService.flyTo.bind(cameraViewService),
					fitBounds: cameraViewService.fitBounds.bind(cameraViewService),
				},
				pmTiles: {
					addLayer: pmTilesService.addLayer.bind(pmTilesService),
					removeLayer: pmTilesService.removeLayer.bind(pmTilesService),
					activateLayer: (id: PmTilesSourceId) => pmTilesService.updateSourceDisplaySettings(id, { isVisible: true }),
					deactivateLayer: (id: PmTilesSourceId) => pmTilesService.updateSourceDisplaySettings(id, { isVisible: false }),
					setOpacity: (id: PmTilesSourceId, opacity: number) => pmTilesService.updateSourceDisplaySettings(id, { opacity: Math.max(0, Math.min(1, opacity)) }),
					reorderLayers: pmTilesRenderingService.reorderLayers.bind(pmTilesRenderingService),
				},
			},
		};
	}
}

export { MapLibreModule };
